import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const DEFAULT_SECTIONS = [
  { section_type: "banner", order_index: 0, title: "Banner", is_visible: false },
  { section_type: "countdown", order_index: 1, title: "Countdown", is_visible: false },
  { section_type: "hero", order_index: 2, title: "Hero", is_visible: false },
  { section_type: "about", order_index: 3, title: "Sobre", is_visible: false },
  { section_type: "benefits", order_index: 4, title: "Benefícios", is_visible: false },
  { section_type: "cta", order_index: 5, title: "CTA", is_visible: false },
];

interface CreatePageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (newPageId: string) => void;
}

export function CreatePageDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreatePageDialogProps) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const slug = slugify(name);

  const handleCreate = async () => {
    if (!name.trim() || !slug) return;
    setLoading(true);

    try {
      const { data: newPage, error: pageError } = await supabase
        .from("partner_landing_pages")
        .insert({
          slug,
          page_title: name.trim(),
          is_active: false,
        })
        .select("id")
        .single();

      if (pageError) throw pageError;

      const sections = DEFAULT_SECTIONS.map((s) => ({
        ...s,
        landing_page_id: newPage.id,
        content: "",
        subtitle: "",
        buttons: [],
      }));

      const { error: secError } = await supabase
        .from("partner_landing_sections")
        .insert(sections);

      if (secError) throw secError;

      toast.success("Nova LP criada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["admin-partner-pages"] });
      setName("");
      onOpenChange(false);
      onSuccess(newPage.id);
    } catch (err: any) {
      toast.error(err.message || "Erro ao criar LP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nova Landing Page</DialogTitle>
          <DialogDescription>
            Informe o nome do parceiro. A LP será criada com seções padrão e inativa.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="new-partner-name">Nome do parceiro</Label>
            <Input
              id="new-partner-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Escola Vostê"
            />
          </div>
          {slug && (
            <p className="text-sm text-muted-foreground">
              Slug: <span className="font-mono text-foreground">{slug}</span>
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={!name.trim() || loading}>
            {loading ? "Criando..." : "Criar LP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
